import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Users as UsersIcon, MessageSquare } from "lucide-react";
import { api, apiError, fmtDate, waLink } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export function ExecutivesManager() {
  const [execs, setExecs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", email: "" });
  const [created, setCreated] = useState(null);

  const load = () =>
    api.get("/central-sales/executives").then(({ data }) => setExecs(data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Enter the executive's name");
    if (form.mobile.replace(/\D/g, "").length !== 10) return toast.error("Enter a valid 10-digit mobile");
    try {
      const { data } = await api.post("/central-sales/executives", {
        name: form.name.trim(), mobile: form.mobile.trim(), email: form.email.trim(),
      });
      setCreated(data);
      setForm({ name: "", mobile: "", email: "" });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <Card className="mb-6 border-brand-line shadow-none" data-testid="executives-manager">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-lg">
          <UsersIcon className="h-5 w-5 text-brand" /> My Sales Executives
        </CardTitle>
        <Button data-testid="add-executive-btn" onClick={() => { setCreated(null); setOpen(true); }}>
          <UserPlus className="mr-2 h-4 w-4" /> Add executive
        </Button>
      </CardHeader>
      <CardContent>
        {execs.length === 0 ? (
          <p className="py-4 text-sm text-slate-500">
            No executives yet. Add one to assign B2B detailer leads to them.
          </p>
        ) : (
          <div className="divide-y divide-brand-line" data-testid="executives-list">
            {execs.map((e) => (
              <div key={e.id} className="flex items-center justify-between py-2.5" data-testid={`exec-row-${e.id}`}>
                <div>
                  <p className="font-medium text-brand-ink">{e.name}</p>
                  <p className="text-xs text-slate-500">
                    {e.mobile}{e.email ? ` · ${e.email}` : ""} · added {fmtDate(e.created_at)}
                  </p>
                </div>
                <Badge className={`border-0 ${e.pin_set && e.email_verified ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                  {e.pin_set && e.email_verified ? "Active" : "Invite pending"}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-executive-dialog">
          <DialogHeader>
            <DialogTitle>Add Central Sales Executive (Products)</DialogTitle>
            <DialogDescription>
              They log in with their mobile and a first-time code (first 4 letters of name + last 4
              digits of mobile), then verify email and set their own PIN.
            </DialogDescription>
          </DialogHeader>
          {created ? (
            <div className="space-y-3" data-testid="exec-created-info">
              <p className="text-sm text-slate-700">
                <b>{created.user.name}</b> added as Central Sales Executive (Products).
              </p>
              <div className="rounded-md bg-slate-50 p-3 text-sm">
                <p>Mobile: <b>{created.user.mobile}</b></p>
                <p>First-time login code: <b data-testid="exec-default-code">{created.default_login_code}</b></p>
                <p className="mt-1 text-xs text-slate-500">
                  Share these so they can sign in from the Central team tab.
                  {created.invite_sent ? " An invite email was also sent." : ""}
                </p>
              </div>
              <Button
                variant="outline"
                className="w-full border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="exec-send-whatsapp-btn"
                onClick={() => {
                  const msg =
                    `Welcome to the team. Your login details for Veloskin PPF Network: ` +
                    `Mobile: ${created.user.mobile}, Password: ${created.default_login_code}. ` +
                    `Please log in and change your password after first login.`;
                  window.open(`${waLink(created.user.mobile)}?text=${encodeURIComponent(msg)}`, "_blank");
                }}
              >
                <MessageSquare className="mr-2 h-4 w-4" /> Send Login Details via WhatsApp
              </Button>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreated(null)}>Add another</Button>
                <Button data-testid="exec-done-btn" onClick={() => setOpen(false)}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                <div>
                  <Label>Name *</Label>
                  <Input data-testid="exec-name-input" value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                </div>
                <div>
                  <Label>Mobile number *</Label>
                  <Input data-testid="exec-mobile-input" inputMode="numeric" placeholder="10-digit mobile"
                    value={form.mobile}
                    onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))} />
                </div>
                <div>
                  <Label>Email (optional)</Label>
                  <Input data-testid="exec-email-input" value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="save-executive-btn" onClick={submit}>Add executive</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export function ExecScorecard() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/central-sales/scorecard").then(({ data }) => setRows(data.executives || [])).catch(() => {});
  }, []);
  if (!rows.length) return null;
  const fmt = (ts) => (ts ? fmtDate(ts) : "—");
  return (
    <Card className="mb-6 border-brand-line shadow-none" data-testid="exec-scorecard">
      <CardHeader>
        <CardTitle className="text-lg">Executive performance</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="exec-scorecard-table">
          <thead>
            <tr className="border-b border-brand-line text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="py-2">Executive</th>
              <th className="py-2 text-right">Calls</th>
              <th className="py-2 text-right">Interactions</th>
              <th className="py-2 text-right">Active pipeline</th>
              <th className="py-2 text-right">Converted</th>
              <th className="py-2 text-right">Conv %</th>
              <th className="py-2 text-right">Last activity</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.exec_id} className="border-b border-brand-line/60" data-testid={`scorecard-row-${r.exec_id}`}>
                <td className="py-2.5 font-medium text-brand-ink">{r.name}</td>
                <td className="py-2.5 text-right">{r.calls}</td>
                <td className="py-2.5 text-right">{r.interactions}</td>
                <td className="py-2.5 text-right">{r.active_pipeline}</td>
                <td className="py-2.5 text-right font-semibold text-emerald-600">{r.converted}</td>
                <td className="py-2.5 text-right">{r.conversion_rate}%</td>
                <td className="py-2.5 text-right text-slate-500">{fmt(r.last_activity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
